import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { items, sortedByHeat } from "@/mocks/items";
import { hotTags } from "@/mocks/site";
import NewsCard from "@/components/base/NewsCard";
import SectionHeading from "@/components/base/SectionHeading";

const tabs = [
  { key: "all", label: "全部", icon: "ri-stack-line" },
  { key: "huati", label: "热门话题", icon: "ri-chat-3-line" },
  { key: "shijian", label: "热点事件", icon: "ri-focus-3-line" },
  { key: "renwu", label: "人物动态", icon: "ri-user-heart-line" },
  { key: "jigou", label: "机构观察", icon: "ri-building-4-line" },
];

export default function FeedSection() {
  const [active, setActive] = useState("all");
  const top = sortedByHeat().slice(0, 5);

  const list = useMemo(
    () => (active === "all" ? items.slice(0, 6) : items.filter((i) => i.cat === active)),
    [active]
  );

  return (
    <section className="w-full bg-background-100 py-10 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="实时更新"
          title="最新内容流"
          desc="按分类筛选，第一时间看大家都在关注什么。"
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.7fr_1fr]">
          {/* 左侧内容流 */}
          <div>
            <div className="mb-5 inline-flex flex-wrap gap-1 rounded-full bg-background-200/70 p-1">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActive(t.key)}
                  className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                    active === t.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  <i className={`${t.icon} text-base`}></i>
                  {t.label}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-4">
              {list.map((item) => (
                <NewsCard key={item.id} item={item} horizontal />
              ))}
            </div>
          </div>

          {/* 右侧热榜 */}
          <aside className="flex flex-col gap-6">
            <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
              <div className="flex items-center gap-2 border-b border-background-200 px-5 py-4">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-fire-fill"></i>
                </span>
                <h3 className="font-heading text-lg font-bold text-foreground-950">今日热榜</h3>
                <span className="ml-auto text-xs text-foreground-400">更新于刚刚</span>
              </div>
              <ol className="divide-y divide-background-100">
                {top.map((item, idx) => (
                  <li key={item.id}>
                    <Link
                      to={`/item/${item.id}`}
                      className="flex cursor-pointer items-center gap-3 px-5 py-3.5 transition-colors hover:bg-background-100"
                    >
                      <span
                        className={`font-heading text-xl font-black ${
                          idx < 3 ? "text-primary-500" : "text-foreground-300"
                        }`}
                      >
                        {idx + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-foreground-900">{item.title}</span>
                        <span className="mt-0.5 flex items-center gap-2 text-xs text-foreground-500">
                          <i className="ri-fire-line text-primary-500"></i>
                          {item.heat.toLocaleString()} 热度
                        </span>
                      </span>
                      {idx < 3 && (
                        <span className="shrink-0 rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-bold text-primary-700">
                          爆
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-50 p-5">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-500 text-background-50">
                  <i className="ri-hashtag"></i>
                </span>
                <h3 className="font-heading text-lg font-bold text-foreground-950">热搜标签</h3>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {hotTags.map((t) => (
                  <Link
                    key={t.name}
                    to={`/category/${t.cat}`}
                    className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-background-100 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-accent-100 hover:text-accent-800"
                  >
                    <i className="ri-fire-line text-accent-500"></i>
                    {t.name}
                    <span className="text-foreground-400">{t.heat}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-background-200/70 bg-gradient-to-br from-primary-100 to-secondary-100 p-5">
              <span className="inline-flex items-center gap-1 rounded-full bg-background-50/80 px-3 py-1 text-xs font-semibold text-primary-700">
                <i className="ri-quill-pen-line"></i>编辑部提示
              </span>
              <p className="mt-3 text-sm leading-6 text-foreground-700">
                内容来自公开讨论与站内整理，涉及数据仅供参考。看到可疑信息？欢迎帮助我们核查纠错。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}